import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/serge'
import DeliverableDropdown from '@/components/serge-workspace/DeliverableDropdown'
import { PaymentScheduleFormModal } from '@/components/serge-workspace/PaymentScheduleFormModal'
import type { PaymentScheduleFormData } from '@/lib/paymentScheduleFormSchema'
import { 
  DELIVERABLE_LABELS,
  getSergeProjectContext,
  getSergeAvailableDeliverables,
  generateSergeDeliverable,
  getSergeDeliverableHistory,
  getSergeN8nContext,
  updateSergeContext,
  resetSergeContext,
  getSergeLegalEntities
} from '@/services/sergeApi'
import { Loader2, Check, Clock, Edit3, Trash2, X, Save, Maximize2, Minimize2, ChevronLeft, ChevronRight } from 'lucide-react'
import { getDeliverableEstimateLabel, getMissingRecommendedPrerequisites, getRecommendedDeliverables, isDeliverableImplemented, isDeliverableType } from '@/lib/deliverableOrder'

const HIDDEN_EXPORT_DELIVERABLE_TYPES = new Set(['workflow'])
const ORDER_PAGE_SIZE = 7

type DeliverableType = string
type ProjectStep = 'import' | 'analysis' | 'clarification' | 'risks' | 'export'

interface StepStatus {
  status?: string
  isCurrent?: boolean
}

interface ExportDeliverable {
  type: string
  label: string
  description: string
}

interface DeliverableHistory {
  id: number
  version: string | number
  createdAt: string
  filePath?: string
  fileName?: string
  status?: string
}

interface LegalEntity {
  id: number
  name: string
  legalName?: string
}

interface GeneratingStatus {
  [key: string]: 'idle' | 'generating' | 'completed' | 'error'
}

interface FunctionalSpecsSummary {
  modules?: unknown[]
  useCases?: unknown[]
  businessRules?: unknown[]
  nonFunctionalRequirements?: unknown
  documentLink?: string
  version?: string
  updatedAt?: string
}

interface UserStoriesSummary {
  userStories?: Array<{
    id: string
    title: string
    epic?: string
    priority?: string
    points?: number
    sprint?: string
    status?: string
  }>
  epics?: Array<{
    id: string
    name: string
    description?: string
  }>
  sprints?: Array<{
    number: number
    name: string
    startDate?: string
    endDate?: string
  }>
  totalPoints?: number
  documentLink?: string
  version?: string
  updatedAt?: string
}

interface ProjectContextData {
  projectId?: number
  projectName?: string
  description?: string
  steps?: {
    import?: unknown
    analysis?: unknown
    clarification?: unknown
    risks?: unknown
    export?: {
      status?: string
      selectedDeliverables?: string[]
      generatedFiles?: unknown[]
      functionalSpecsSummary?: FunctionalSpecsSummary
      technicalSpecsSummary?: unknown
      summaryUserStories?: UserStoriesSummary
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  currentStep?: string
  createdAt?: string
  updatedAt?: string
  [key: string]: unknown
}

export default function SergeExportPage() {
  const { projectId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [deliverables, setDeliverables] = useState<ExportDeliverable[]>([])
  const [generatingStatus, setGeneratingStatus] = useState<GeneratingStatus>({})
  const [projectContext, setProjectContext] = useState<ProjectContextData | null>(null)
  const [stepsStatus, setStepsStatus] = useState<Record<ProjectStep, StepStatus> | null>(null)
  const [deliverableHistories, setDeliverableHistories] = useState<Record<string, DeliverableHistory[]>>({})
  const [orderPage, setOrderPage] = useState(0)

  // Context editor state
  const [isEditing, setIsEditing] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [editedContext, setEditedContext] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  // Legal entities state
  const [legalEntities, setLegalEntities] = useState<LegalEntity[]>([])
  const [selectedLegalEntityId, setSelectedLegalEntityId] = useState<number | null>(null)
  const [isLegalEntitiesLoading, setIsLegalEntitiesLoading] = useState(false)

  // Payment schedule modal state
  const [showPaymentScheduleModal, setShowPaymentScheduleModal] = useState(false)
  const [pendingPaymentScheduleType, setPendingPaymentScheduleType] = useState<string | null>(null)

  useEffect(() => {
    if (projectId) {
      loadDeliverables()
    }
  }, [projectId])

  useEffect(() => {
    loadLegalEntities()
  }, [])

  const loadLegalEntities = async () => {
    try {
      setIsLegalEntitiesLoading(true)
      const data = await getSergeLegalEntities()
      const list = Array.isArray(data) ? data : (data as { 'hydra:member'?: LegalEntity[] })['hydra:member'] || []
      setLegalEntities(list)
      if (list.length > 0 && !selectedLegalEntityId) {
        setSelectedLegalEntityId(list[0].id)
      }
    } catch {
      toast.error('Erreur lors du chargement des entités légales')
    } finally {
      setIsLegalEntitiesLoading(false)
    }
  }

  const availableDeliverableTypes = useMemo(() => {
    const set = new Set<DeliverableType>()
    deliverables.forEach((d) => {
      if (isDeliverableType(d.type)) set.add(d.type)
    })
    return set
  }, [deliverables])

  const recommendedDeliverableTypes = useMemo(
    () => getRecommendedDeliverables(availableDeliverableTypes),
    [availableDeliverableTypes]
  )

  const generatedDeliverableTypes = useMemo(() => {
    const set = new Set<DeliverableType>()
    Object.entries(deliverableHistories).forEach(([type, history]) => {
      if (history?.length && isDeliverableType(type)) set.add(type)
    })
    return set
  }, [deliverableHistories])

  const nextRecommendedDeliverable = useMemo(() => {
    return recommendedDeliverableTypes.find((type) => !generatedDeliverableTypes.has(type)) ?? null
  }, [generatedDeliverableTypes, recommendedDeliverableTypes])

  const orderTotalPages = useMemo(
    () => Math.max(1, Math.ceil(recommendedDeliverableTypes.length / ORDER_PAGE_SIZE)),
    [recommendedDeliverableTypes.length]
  )

  useEffect(() => {
    setOrderPage((prev) => Math.min(prev, orderTotalPages - 1))
  }, [orderTotalPages])

  useEffect(() => {
    if (!nextRecommendedDeliverable) return
    const index = recommendedDeliverableTypes.indexOf(nextRecommendedDeliverable)
    if (index < 0) return
    const desiredPage = Math.floor(index / ORDER_PAGE_SIZE)
    setOrderPage(desiredPage)
  }, [nextRecommendedDeliverable, recommendedDeliverableTypes])

  const paginatedRecommendedDeliverableTypes = useMemo(() => {
    const start = orderPage * ORDER_PAGE_SIZE
    return recommendedDeliverableTypes.slice(start, start + ORDER_PAGE_SIZE)
  }, [orderPage, recommendedDeliverableTypes])

  const orderedDeliverables = useMemo(() => {
    const rank = new Map(recommendedDeliverableTypes.map((t, i) => [t, i]))
    return [...deliverables].sort((a, b) => {
      const aKnown = isDeliverableType(a.type)
      const bKnown = isDeliverableType(b.type)
      if (!aKnown && !bKnown) return a.label.localeCompare(b.label, 'fr')
      if (aKnown && !bKnown) return -1
      if (!aKnown && bKnown) return 1
      const aRank = rank.get(a.type as DeliverableType) ?? 999
      const bRank = rank.get(b.type as DeliverableType) ?? 999
      return aRank - bRank
    })
  }, [deliverables, recommendedDeliverableTypes])

  const getDeliverableLabel = (type: DeliverableType) =>
    deliverables.find((d) => d.type === type)?.label ?? DELIVERABLE_LABELS[type]

  const loadDeliverables = async () => {
    try {
      setIsLoading(true)
      const [deliverablesResult, contextResult, stepsResult] = await Promise.all([
        getSergeAvailableDeliverables(Number(projectId)),
        getSergeN8nContext(Number(projectId)),
        getSergeProjectContext(Number(projectId)),
      ])
      const visibleDeliverables = deliverablesResult.deliverables.filter(
        (deliverable) => !HIDDEN_EXPORT_DELIVERABLE_TYPES.has(deliverable.type)
      )
      setDeliverables(visibleDeliverables)
      setProjectContext(contextResult as ProjectContextData)
      setStepsStatus(stepsResult.stepsStatus)

      // Charger l'historique pour chaque type de livrable
      const histories: Record<string, DeliverableHistory[]> = {}
      await Promise.all(
        visibleDeliverables.map(async (d) => {
          try {
            const historyResult = await getSergeDeliverableHistory(Number(projectId), d.type as DeliverableType)
            histories[d.type] = historyResult.history
          } catch {
            histories[d.type] = []
          }
        })
      )
      setDeliverableHistories(histories)
    } catch {
      toast.error('Erreur lors du chargement des livrables')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGenerateOne = async (type: string, extraData?: Record<string, unknown>) => {
    try {
      if (!projectId) {
        throw new Error('Projet introuvable.')
      }

      if (!selectedLegalEntityId) {
        toast.error('Veuillez sélectionner une entité légale.')
        return
      }

      // Si c'est un échéancier de paiement et qu'on n'a pas de données supplémentaires, afficher le modal
      if (type === 'payment_schedule' && !extraData) {
        setPendingPaymentScheduleType(type)
        setShowPaymentScheduleModal(true)
        return
      }

      if (isDeliverableType(type) && !isDeliverableImplemented(type)) {
        toast.error('Ce livrable n est pas encore implemente.')
        return
      }

      if (stepsStatus) {
        const importDone = stepsStatus.import?.status === 'completed'
        const analysisDone = stepsStatus.analysis?.status === 'completed'
        if (!importDone || !analysisDone) {
          const ok = window.confirm(
            "Les étapes Import et/ou Analyse ne sont pas terminées. La génération risque d'être incomplète ou incohérente.\n\nSouhaitez-vous continuer ?"
          )
          if (!ok) return
        }
      }

      if (isDeliverableType(type)) {
        const missing = getMissingRecommendedPrerequisites(
          type,
          generatedDeliverableTypes,
          availableDeliverableTypes
        )
        if (missing.length) {
          const missingList = missing.map(getDeliverableLabel).join(', ')
          const ok = window.confirm(
            `Vous êtes sur le point de générer « ${getDeliverableLabel(type)} ».\n\nPour un résultat cohérent, il est recommandé de générer d'abord :\n${missingList}\n\nSouhaitez-vous continuer quand même ?`
          )
          if (!ok) return
        }
      }

      setGeneratingStatus((prev) => ({ ...prev, [type]: 'generating' }))

      const result = await generateSergeDeliverable(Number(projectId), type, selectedLegalEntityId, extraData)

      if (result.success) {
        setGeneratingStatus((prev) => ({ ...prev, [type]: 'completed' }))
        toast.success(`Livrable généré: ${result.deliverableType} v${result.version}`)

        // Recharger les livrables et l'historique
        await loadDeliverables()
      } else {
        throw new Error('Erreur lors de la génération')
      }
    } catch (error) {
      setGeneratingStatus((prev) => ({ ...prev, [type]: 'error' }))
      const message = error instanceof Error ? error.message : `Erreur: ${type}`
      toast.error(message)
    }
  }

  const handleGenerateFromRecommended = async (type: DeliverableType) => {
    const index = recommendedDeliverableTypes.indexOf(type)
    if (index >= 0) setOrderPage(Math.floor(index / ORDER_PAGE_SIZE))
    await handleGenerateOne(type)
  }

  const handlePaymentScheduleSubmit = async (data: PaymentScheduleFormData) => {
    setShowPaymentScheduleModal(false)
    if (pendingPaymentScheduleType) {
      await handleGenerateOne(pendingPaymentScheduleType, { paymentScheduleData: data })
      setPendingPaymentScheduleType(null)
    }
  }

  const handleStartEdit = () => {
    setEditedContext(JSON.stringify(projectContext, null, 2))
    setIsEditing(true)
    setIsExpanded(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedContext('')
  }

  const handleSaveContext = async () => {
    try {
      const parsed = JSON.parse(editedContext)
      setIsSaving(true)
      const result = await updateSergeContext(Number(projectId), parsed.steps || parsed)
      setProjectContext((prev) => ({
        ...prev,
        steps: result.context as ProjectContextData['steps']
      }))
      toast.success('Contexte mis a jour')
      setIsEditing(false)
      loadDeliverables()
    } catch (e) {
      if (e instanceof SyntaxError) {
        toast.error('JSON invalide')
      } else {
        toast.error('Erreur lors de la sauvegarde')
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetContext = async () => {
    if (!confirm('Reinitialiser le contexte ? Toutes les donnees d\'analyse, clarification et risques seront supprimees.')) {
      return
    }

    try {
      setIsResetting(true)
      await resetSergeContext(Number(projectId))
      toast.success('Contexte reinitialise')
      loadDeliverables()
    } catch {
      toast.error('Erreur lors de la reinitialisation')
    } finally {
      setIsResetting(false)
    }
  }

  const getStatusIcon = (type: string) => {
    const status = generatingStatus[type]
    switch (status) {
      case 'generating':
        return <Loader2 className="w-4 h-4 animate-spin text-primary" />
      case 'completed':
        return <Check className="w-4 h-4 text-green-500" />
      case 'error':
        return <span className="text-red-500 text-xs">Erreur</span>
      default:
        return <Clock className="w-4 h-4 text-foreground-muted" />
    }
  }

  const getStepStatusIcon = (status?: StepStatus) => {
    if (!status) {
      return <Clock className="w-4 h-4 text-foreground-muted" />
    }
    if (status.status === 'completed') {
      return <Check className="w-4 h-4 text-green-500" />
    }
    if (status.status === 'in_progress' || status.isCurrent) {
      return <Loader2 className="w-4 h-4 animate-spin text-primary" />
    }
    return <Clock className="w-4 h-4 text-foreground-muted" />
  }

  const getStepStatusLabel = (status?: StepStatus) => {
    if (!status) {
      return 'Inconnu'
    }
    if (status.status === 'completed') {
      return 'Termine'
    }
    if (status.status === 'in_progress' || status.isCurrent) {
      return 'En cours'
    }
    return 'En attente'
  }

  const stepLabels: Array<{ key: ProjectStep; label: string }> = [
    { key: 'import', label: 'Import' },
    { key: 'analysis', label: 'Analyse' },
    { key: 'clarification', label: 'Clarification' },
    { key: 'risks', label: 'Risques' },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      <section className="col-span-12 xl:col-span-8 space-y-4">
        <div className="border border-border bg-surface p-5">
          <h2 className="text-lg font-semibold text-foreground">Generer les livrables</h2>
          <p className="text-sm text-foreground-secondary mt-2">
            Generez vos livrables un par un via les workflows n8n.
            Le contexte complet du projet sera envoye automatiquement.
          </p>

          {recommendedDeliverableTypes.length > 0 && (
            <div className="mt-4 border border-border bg-bg p-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-foreground-muted uppercase tracking-[0.35em]">
                    Ordre recommande
                  </p>
                  <p className="text-sm text-foreground-secondary mt-2">
                    Pour maximiser la coherence entre livrables, suivez l ordre ci-dessous (progressive disclosure) :
                    chaque document enrichit le contexte du suivant.
                  </p>
                </div>
                {nextRecommendedDeliverable && (
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-xs font-mono text-foreground-muted">Etape suivante</div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleGenerateFromRecommended(nextRecommendedDeliverable)}
                      disabled={
                        generatingStatus[nextRecommendedDeliverable] === 'generating' ||
                        !isDeliverableImplemented(nextRecommendedDeliverable)
                      }
                    >
                      Generer : {getDeliverableLabel(nextRecommendedDeliverable)}
                    </Button>
                    {getDeliverableEstimateLabel(nextRecommendedDeliverable) && (
                      <div className="text-xs font-mono text-foreground-muted">
                        {getDeliverableEstimateLabel(nextRecommendedDeliverable)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="text-xs font-mono text-foreground-muted">
                  Page {orderPage + 1}/{orderTotalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="h-10 w-10 border border-border text-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center disabled:opacity-50 disabled:pointer-events-none"
                    onClick={() => setOrderPage((p) => Math.max(0, p - 1))}
                    disabled={orderPage === 0}
                    title="Page precedente"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="h-10 w-10 border border-border text-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center disabled:opacity-50 disabled:pointer-events-none"
                    onClick={() => setOrderPage((p) => Math.min(orderTotalPages - 1, p + 1))}
                    disabled={orderPage >= orderTotalPages - 1}
                    title="Page suivante"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2">
                {paginatedRecommendedDeliverableTypes.map((type) => {
                  const globalIndex = recommendedDeliverableTypes.indexOf(type)
                  const isDone = generatedDeliverableTypes.has(type)
                  const isNext = nextRecommendedDeliverable === type
                  const isGenerating = generatingStatus[type] === 'generating'
                  const estimateLabel = getDeliverableEstimateLabel(type)
                  const isAvailable = isDeliverableImplemented(type)
                  return (
                    <div
                      key={type}
                      className={`border border-border ${
                        isGenerating
                          ? 'bg-primary/10 border-primary/40 animate-pulse'
                          : isNext
                            ? 'bg-primary/5 border-primary/40'
                            : 'bg-surface'
                      } p-3 transition-colors`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-mono text-foreground-muted w-10">
                            #{globalIndex + 1}
                          </span>
                          <span className="text-sm font-mono text-foreground truncate">
                            {getDeliverableLabel(type)}
                          </span>
                          {estimateLabel && (
                            <span className="text-xs font-mono text-foreground-muted whitespace-nowrap">
                              {estimateLabel}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {isGenerating ? (
                            <span className="flex items-center gap-2 text-xs font-mono text-primary">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generation...
                            </span>
                          ) : isDone ? (
                            <span className="flex items-center gap-2 text-xs font-mono text-green-500">
                              <Check className="w-4 h-4" />
                              Genere
                            </span>
                          ) : !isAvailable ? (
                            <span className="text-xs font-mono text-foreground-muted">Non disponible</span>
                          ) : isNext ? (
                            <span className="text-xs font-mono text-primary">Suivant</span>
                          ) : (
                            <span className="text-xs font-mono text-foreground-muted">En attente</span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGenerateFromRecommended(type)}
                            disabled={isGenerating || !isAvailable}
                          >
                            Generer
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {orderedDeliverables.map((deliverable) => {
              const rank =
                isDeliverableType(deliverable.type) &&
                recommendedDeliverableTypes.includes(deliverable.type)
                  ? recommendedDeliverableTypes.indexOf(deliverable.type) + 1
                  : null

              const estimate =
                isDeliverableType(deliverable.type) ? getDeliverableEstimateLabel(deliverable.type) : null
              const generationAvailable =
                isDeliverableType(deliverable.type) ? isDeliverableImplemented(deliverable.type) : true

              return (
                <div
                  key={deliverable.type}
                  className="border border-border bg-bg p-4 transition-colors hover:border-border-hover"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground">{deliverable.label}</h3>
                        {rank !== null && (
                          <span className="text-xs font-mono text-primary">
                            #{rank}
                          </span>
                        )}
                        {estimate && (
                          <span className="text-xs font-mono text-foreground-muted whitespace-nowrap">
                            {estimate}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-muted mt-1">{deliverable.description}</p>
                      {!generationAvailable && (
                        <p className="text-xs text-foreground-muted mt-2 italic">
                          Non implemente
                        </p>
                      )}
                    </div>
                    {getStatusIcon(deliverable.type)}
                  </div>
                  <div className="mt-3">
                    <DeliverableDropdown
                      label={deliverable.label}
                      history={deliverableHistories[deliverable.type] || []}
                      onGenerate={() => handleGenerateOne(deliverable.type)}
                      isGenerating={generatingStatus[deliverable.type] === 'generating'}
                      disabledReason={!generationAvailable ? 'Non implemente' : undefined}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="col-span-12 xl:col-span-4 space-y-4">
        {/* Sélecteur d'entité légale */}
        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Entité légale
          </h3>
          <p className="text-sm text-foreground-secondary mt-3">
            Sélectionnez l'entité légale à utiliser pour la génération des documents.
          </p>
          <div className="mt-4">
            {isLegalEntitiesLoading ? (
              <div className="flex items-center gap-2 text-sm text-foreground-muted">
                <Loader2 className="w-4 h-4 animate-spin" />
                Chargement...
              </div>
            ) : legalEntities.length === 0 ? (
              <div className="text-sm text-amber-400">
                Aucune entité légale configurée. Veuillez en créer une dans Serge.
              </div>
            ) : (
              <select
                value={selectedLegalEntityId || ''}
                onChange={(e) => setSelectedLegalEntityId(Number(e.target.value) || null)}
                className="w-full border border-border bg-bg p-2 text-sm text-foreground focus:outline-none focus:border-primary"
              >
                {legalEntities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name} {entity.legalName ? `(${entity.legalName})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Contexte du projet
          </h3>
          <p className="text-sm text-foreground-secondary mt-3">
            Le contexte complet du projet (analyse, clarifications, risques) sera envoye a n8n
            pour generer chaque livrable.
          </p>
          <div className="mt-4">
            <Button variant="outline" size="sm" className="w-full" onClick={loadDeliverables}>
              Rafraichir
            </Button>
          </div>
        </div>

        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Statut des etapes
          </h3>
          <ul className="mt-4 space-y-2">
            {stepLabels.map((step) => (
              <li key={step.key} className="flex items-center justify-between text-sm">
                <span className="text-foreground-secondary">{step.label}</span>
                <span className="flex items-center gap-2 text-xs font-mono text-foreground-muted">
                  {getStepStatusLabel(stepsStatus?.[step.key])}
                  {getStepStatusIcon(stepsStatus?.[step.key])}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-border bg-surface p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
              Contexte complet
            </h3>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-foreground-muted hover:text-foreground transition-colors"
                    title={isExpanded ? 'Reduire' : 'Agrandir'}
                  >
                    {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    className="text-foreground-muted hover:text-primary transition-colors"
                    title="Modifier"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleResetContext}
                    disabled={isResetting}
                    className="text-foreground-muted hover:text-red-500 transition-colors disabled:opacity-50"
                    title="Reinitialiser"
                  >
                    {isResetting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {isEditing ? (
            <div className="mt-3 space-y-3">
              <textarea
                value={editedContext}
                onChange={(e) => setEditedContext(e.target.value)}
                className="w-full h-96 p-3 bg-bg border border-border text-xs font-mono text-foreground resize-none focus:border-primary focus:outline-none"
                spellCheck={false}
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveContext}
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Sauvegarder
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2"
                >
                  <X className="w-3 h-3" />
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div className={`mt-3 border border-border bg-bg p-3 overflow-auto ${isExpanded ? 'max-h-[600px]' : 'max-h-48'}`}>
              <pre className="text-xs text-foreground-muted font-mono whitespace-pre-wrap">
                {JSON.stringify(projectContext, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* summarySpecFunctional (n8n) — stocké côté API dans steps.export.functionalSpecsSummary */}
        <div className="border border-border bg-surface p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
              summarySpecFunctional
            </h3>
          </div>
          <p className="text-sm text-foreground-secondary mt-3">
            Résumé structuré généré par n8n (clé `summarySpecFunctional`), persisté dans le contexte projet sous
            `steps.export.functionalSpecsSummary` pour servir de contexte aux livrables suivants (ex: specs techniques).
          </p>

          {projectContext?.steps?.export?.functionalSpecsSummary ? (
            <>
              <div className="mt-4 border border-border bg-bg p-3 overflow-auto max-h-96">
                <pre className="text-xs text-foreground-muted font-mono whitespace-pre-wrap">
                  {JSON.stringify(projectContext.steps.export.functionalSpecsSummary, null, 2)}
                </pre>
              </div>

              {/* Statistiques rapides */}
              {(() => {
                const summary = projectContext.steps.export.functionalSpecsSummary
                const modulesCount = Array.isArray(summary.modules) ? summary.modules.length : 0
                const useCasesCount = Array.isArray(summary.useCases) ? summary.useCases.length : 0
                const rulesCount = Array.isArray(summary.businessRules) ? summary.businessRules.length : 0

                return (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="border border-border bg-bg p-3">
                      <div className="text-xs text-foreground-muted uppercase tracking-widest">Modules</div>
                      <div className="text-2xl font-bold text-primary mt-1">{modulesCount}</div>
                    </div>
                    <div className="border border-border bg-bg p-3">
                      <div className="text-xs text-foreground-muted uppercase tracking-widest">Cas d'usage</div>
                      <div className="text-2xl font-bold text-primary mt-1">{useCasesCount}</div>
                    </div>
                    <div className="border border-border bg-bg p-3">
                      <div className="text-xs text-foreground-muted uppercase tracking-widest">Regles metier</div>
                      <div className="text-2xl font-bold text-primary mt-1">{rulesCount}</div>
                    </div>
                    {summary.version && (
                      <div className="border border-border bg-bg p-3">
                        <div className="text-xs text-foreground-muted uppercase tracking-widest">Version</div>
                        <div className="text-lg font-bold text-foreground mt-1">{summary.version}</div>
                      </div>
                    )}
                    {summary.documentLink && (
                      <div className="col-span-2 border border-border bg-bg p-3">
                        <div className="text-xs text-foreground-muted uppercase tracking-widest mb-2">Document</div>
                        <a
                          href={summary.documentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline break-all"
                        >
                          Ouvrir dans Google Docs
                        </a>
                      </div>
                    )}
                    {summary.updatedAt && (
                      <div className="col-span-2 border border-border bg-bg p-3">
                        <div className="text-xs text-foreground-muted uppercase tracking-widest">Derniere mise a jour</div>
                        <div className="text-xs text-foreground mt-1">
                          {new Date(summary.updatedAt).toLocaleString('fr-FR')}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </>
          ) : (
            <div className="mt-4 border border-border bg-bg p-3">
              <p className="text-xs text-foreground-muted">
                Aucun `summarySpecFunctional` disponible pour l’instant. Génère d’abord le livrable
                “Spécifications fonctionnelles”, puis clique “Rafraichir”.
              </p>
            </div>
          )}
        </div>

        {/* summarySpecTechnical (n8n) — stocké côté API dans steps.export.technicalSpecsSummary */}
        <div className="border border-border bg-surface p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
              summarySpecTechnical
            </h3>
          </div>
          <p className="text-sm text-foreground-secondary mt-3">
            Résumé structuré généré par n8n (clé `summarySpecTechnical`), persisté dans le contexte projet sous
            `steps.export.technicalSpecsSummary` pour servir de contexte à la génération du diagramme de classes.
          </p>

          {projectContext?.steps?.export?.technicalSpecsSummary ? (
            <div className="mt-4 border border-border bg-bg p-3 overflow-auto max-h-96">
              <pre className="text-xs text-foreground-muted font-mono whitespace-pre-wrap">
                {JSON.stringify(projectContext.steps.export.technicalSpecsSummary, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="mt-4 border border-border bg-bg p-3">
              <p className="text-xs text-foreground-muted">
                Aucun `summarySpecTechnical` disponible pour l’instant. Génère d’abord le livrable
                “Spécifications techniques”, puis clique “Rafraichir”.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Modal pour l'échéancier de paiement */}
      <PaymentScheduleFormModal
        isOpen={showPaymentScheduleModal}
        onClose={() => {
          setShowPaymentScheduleModal(false)
          setPendingPaymentScheduleType(null)
        }}
        onSubmit={handlePaymentScheduleSubmit}
        isSubmitting={generatingStatus['payment_schedule'] === 'generating'}
        projectName={projectContext?.projectName}
      />
    </div>
  )
}
